import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { RequestStatusPill } from '@/components/RequestStatusPill';
import { Skeleton } from '@/components/Skeleton';
import { WhatsAppButton } from '@/components/WhatsAppButton';
import { discountCellText } from '@/lib/discount-display';
import type { BuyerRequestsTabFilter } from '@/lib/db/buyer';

import { useBuyerRequests } from './DashboardRequestsPage.data';

type TabKey = BuyerRequestsTabFilter;

const TABS: TabKey[] = ['all', 'new', 'in_progress', 'treated', 'closed'];

const TAB_LABEL: Record<TabKey, string> = {
  all: 'buyer.requests.tabs.all',
  new: 'buyer.requests.tabs.new',
  in_progress: 'buyer.requests.tabs.inProgress',
  treated: 'buyer.requests.tabs.processed',
  closed: 'buyer.requests.tabs.closed',
};

export function DashboardRequestsPage() {
  const { t } = useTranslation();
  const [tab, setTab] = useState<TabKey>('all');
  const { state, refresh } = useBuyerRequests(tab);

  return (
    <>
      <header className="buyer-page-head">
        <div>
          <h1 className="buyer-page-head__title">{t('buyer.requests.title')}</h1>
          <p className="buyer-page-head__sub">
            {state.status === 'success'
              ? t('buyer.requests.total', { count: state.requests.length })
              : t('buyer.requests.totalLoading')}
          </p>
        </div>
      </header>

      <div className="buyer-tabs" role="tablist">
        {TABS.map((key) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={tab === key}
            className={`buyer-tab${tab === key ? ' is-active' : ''}`}
            onClick={() => setTab(key)}
          >
            {t(TAB_LABEL[key])}
          </button>
        ))}
      </div>

      {state.status === 'loading' ? (
        <div style={{ display: 'grid', gap: 12 }}>
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} height={56} />
          ))}
        </div>
      ) : null}

      {state.status === 'error' ? (
        <div className="buyer-empty" role="alert">
          <h2 className="buyer-empty__title">{t('buyer.requests.errorTitle')}</h2>
          <p className="buyer-empty__lead">{t('buyer.requests.errorLead')}</p>
          <button type="button" className="m5-btn m5-btn--primary" onClick={refresh}>
            {t('buyer.requests.retry')}
          </button>
        </div>
      ) : null}

      {state.status === 'success' && state.requests.length === 0 ? (
        <div className="buyer-empty">
          <h2 className="buyer-empty__title">{t('buyer.requests.emptyTitle')}</h2>
          <p className="buyer-empty__lead">{t('buyer.requests.emptyLead')}</p>
        </div>
      ) : null}

      {state.status === 'success' && state.requests.length > 0 ? (
        <div style={{ overflowX: 'auto' }}>
          <table className="buyer-data-table">
            <thead>
              <tr>
                <th>{t('buyer.requests.headers.deal')}</th>
                <th>{t('buyer.requests.headers.price')}</th>
                <th>{t('buyer.requests.headers.type')}</th>
                <th>{t('buyer.requests.headers.status')}</th>
                <th>{t('buyer.requests.headers.date')}</th>
                <th>{t('buyer.requests.headers.action')}</th>
              </tr>
            </thead>
            <tbody>
              {state.requests.map((req) => (
                <tr key={req.id}>
                  <td>
                    <div className="buyer-data-table__title">{req.dealTitle}</div>
                    <div className="buyer-data-table__zone">{req.zone}</div>
                  </td>
                  <td>
                    <div className="buyer-data-table__price">{req.priceLabel}</div>
                    <div className="buyer-data-table__discount">
                      {discountCellText(req.dldConfidence, req.dldScope, req.discountPct)}
                    </div>
                  </td>
                  <td className="buyer-data-table__zone">{t(`buyer.requests.type.${req.type}`)}</td>
                  <td>
                    <RequestStatusPill status={req.status} />
                  </td>
                  <td className="buyer-data-table__zone">{req.date}</td>
                  <td>
                    {req.whatsappEnabled ? (
                      <WhatsAppButton size="md" label={t('buyer.requests.followUp')} />
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </>
  );
}
